import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react";
import styled from "styled-components";
import { device, useBreakpoints, LAYOUT_CONSTANTS } from "../lib/breakpoints";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ToolOutlined } from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import {
  trackPageView,
  trackModalInteraction,
  trackFormSubmission,
  trackTimeOnPage,
  trackTabChange,
} from "../helpers/dashboardAnalytics";
import PageHeader from "../components/PageHeader";
import { Card, Skeleton, Row, Col } from "antd";
import HandymanModal from "../components/HandymanModal";
import RepairModal from "../components/RepairModal";
import HandymanModel from "../lib/firebase/handymanModel";
import RepairModel from "../lib/firebase/repairModel";
import { propertyKeys } from "../lib/queryKeys";
import unitModel from "../lib/firebase/unitModel";
import { getDebugMode } from "../helpers/constants";
import { getAllDebugRepairs } from "../helpers/debugData";
import {
  RepairStatsSection,
  HandymenSection,
  RepairTabsSection,
} from "../components/repairs";
import { getDraftActions, approveDraftAction } from "../lib/api/maintenanceApi";

const Repairs = () => {
  const { isMobile } = useBreakpoints();
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("active");
  const tabsRef = useRef(null);
  const openedTicketFromQuery = useRef(null);
  const [isAddHandymanModalVisible, setIsAddHandymanModalVisible] =
    useState(false);
  const [isAddRepairModalVisible, setIsAddRepairModalVisible] = useState(false);
  const [isEditHandymanModalVisible, setIsEditHandymanModalVisible] =
    useState(false);
  const [editingHandyman, setEditingHandyman] = useState(null);
  const [isEditRepairModalVisible, setIsEditRepairModalVisible] =
    useState(false);
  const [editingRepair, setEditingRepair] = useState(null);

  // Debug mode - automatically determined by route
  const isDebugMode = getDebugMode();

  // Load data using useQuery for better caching and performance
  const { data: units = [], isLoading: isUnitsLoading } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  // Load vendors (handymen/contractors) data
  const {
    data: vendors = [],
    isLoading: isVendorsLoading,
    refetch: refetchVendors,
  } = useQuery({
    queryKey: ["handymen", user?.uid],
    queryFn: () => HandymanModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  // Load repair data using the maintenance model or debug data
  const {
    data: repairs = [],
    isLoading: isRepairsLoading,
    refetch: refetchRepairs,
  } = useQuery({
    queryKey: ["repairs", user?.uid, isDebugMode],
    queryFn: () => {
      if (isDebugMode) {
        return Promise.resolve(getAllDebugRepairs());
      }
      return RepairModel.getByOwner(user.uid);
    },
    enabled: !!user?.uid || isDebugMode,
  });

  // Leo draft actions (recommendations) for repairs; used for "Approve & Notify Vendor"
  const { data: draftActions = [], refetch: refetchDraftActions } = useQuery({
    queryKey: ["maintenance", "draft-actions", user?.uid],
    queryFn: () => getDraftActions(),
    enabled: !!user?.uid && !isDebugMode,
  });
  const draftActionsByTicketId = React.useMemo(() => {
    const map = {};
    (draftActions || []).forEach((da) => {
      if (da.ticketId) map[da.ticketId] = da;
    });
    return map;
  }, [draftActions]);

  const handleApproveDraft = async (draftActionId) => {
    await approveDraftAction(draftActionId);
    refetchDraftActions();
  };

  // Set active tab from URL parameter and scroll to tabs if needed
  useLayoutEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["active", "scheduled", "completed"].includes(tabParam)) {
      setActiveTab(tabParam);

      // Track tab change
      trackTabChange(tabParam, "repairs", {
        user_id: user?.uid,
        source: "url_param",
      });

      // Snap to the tabs section to focus on the table
      const scrollToTabs = () => {
        if (tabsRef.current) {
          console.log("Snapping to tabs section...");
          tabsRef.current.scrollIntoView({
            behavior: "auto",
            block: "start",
            inline: "nearest",
          });
        } else {
          console.log("tabsRef.current is null, trying alternative method...");
          // Fallback: scroll to element with repairs-tabs class
          const tabsElement = document.querySelector(".repairs-tabs");
          if (tabsElement) {
            tabsElement.scrollIntoView({
              behavior: "auto",
              block: "start",
              inline: "nearest",
            });
          }
        }
      };

      // Try immediately and with delays
      scrollToTabs();
      setTimeout(scrollToTabs, 100);
      setTimeout(scrollToTabs, 300);
    }
  }, [searchParams, user?.uid]);

  // Deep-link: /repairs?ticketId=… → open that repair on the Repairs page (not Compare bids)
  useEffect(() => {
    const ticketId = searchParams.get("ticketId");
    if (!ticketId || isRepairsLoading) return;
    if (openedTicketFromQuery.current === ticketId) return;

    const repair = repairs.find((r) => r.id === ticketId);
    if (!repair) return;

    openedTicketFromQuery.current = ticketId;

    const status = String(repair.status || "").toLowerCase();
    if (status === "scheduled") setActiveTab("scheduled");
    else if (status === "completed" || status === "closed" || status === "cancelled" || status === "canceled")
      setActiveTab("completed");
    else setActiveTab("active");

    setEditingRepair(repair);
    setIsEditRepairModalVisible(true);

    const next = new URLSearchParams(searchParams);
    next.delete("ticketId");
    setSearchParams(next, { replace: true });

    requestAnimationFrame(() => {
      tabsRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [searchParams, setSearchParams, repairs, isRepairsLoading]);

  // In debug mode, don't wait for units/vendors loading since they're not needed
  const loading = isDebugMode
    ? isRepairsLoading
    : isUnitsLoading || isVendorsLoading || isRepairsLoading;

  // Track page view and time on page
  useEffect(() => {
    trackPageView("repairs", {
      user_id: user?.uid,
      user_email: user?.email,
      active_tab: activeTab,
      repairs_count: repairs?.length || 0,
      handymen_count: vendors?.length || 0,
      units_count: units?.length || 0,
    });

    // Track time on page
    const startTime = Date.now();
    return () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      trackTimeOnPage("repairs", timeSpent);
    };
  }, [
    user?.uid,
    user?.email,
    activeTab,
    repairs?.length,
    vendors?.length,
    units?.length,
  ]);

  // Tab change handler with tracking
  const handleTabChange = (tab) => {
    trackTabChange(tab, "repairs", {
      user_id: user?.uid,
      source: "user_click",
      previous_tab: activeTab,
    });
    setActiveTab(tab);
  };

  // Modal handlers with tracking
  const showAddHandymanModal = () => {
    trackModalInteraction("handyman", "opened", {
      user_id: user?.uid,
      action: "add",
      repairs_count: repairs?.length || 0,
    });
    setIsAddHandymanModalVisible(true);
  };

  const closeAddHandymanModal = () => {
    trackModalInteraction("handyman", "closed", {
      user_id: user?.uid,
      action: "add",
    });
    setIsAddHandymanModalVisible(false);
  };

  const showRepairModal = () => {
    trackModalInteraction("repair", "opened", {
      user_id: user?.uid,
      action: "add",
      repairs_count: repairs?.length || 0,
    });
    setIsAddRepairModalVisible(true);
  };

  const showEditHandymanModal = (handyman) => {
    trackModalInteraction("handyman", "opened", {
      user_id: user?.uid,
      action: "edit",
      handyman_id: handyman?.id,
      handyman_name: handyman?.name,
    });
    setEditingHandyman(handyman);
    setIsEditHandymanModalVisible(true);
  };

  const handleEditHandymanSuccess = () => {
    trackFormSubmission("handyman", "edit", {
      user_id: user?.uid,
      success: true,
    });
    setIsEditHandymanModalVisible(false);
    setEditingHandyman(null);
    // Cache invalidation in the modal will handle the refresh
  };

  const activeRepairs = repairs.filter(
    (r) =>
      r.status === "in_progress" ||
      r.status === "open" ||
      r.status === "assigned" ||
      r.status === "pending"
  );
  const scheduledRepairs = repairs.filter((r) => r.status === "scheduled");
  const completedRepairs = repairs.filter((r) => r.status === "completed");

  if (loading) {
    return (
      <RepairsContainer>
        <PageHeader
          title="Repairs"
          subtitle="Track and manage all maintenance requests and repairs"
          icon={ToolOutlined}
        />
        <RepairsContent>
          <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
            {[1, 2, 3].map((i) => (
              <Col xs={24} sm={8} key={i}>
                <Card>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))}
          </Row>
          <Card>
            <Skeleton active paragraph={{ rows: 8 }} />
          </Card>
        </RepairsContent>
      </RepairsContainer>
    );
  }

  return (
    <>
      <RepairsContainer>
        <PageHeader
          title="Repairs"
          subtitle="Track and manage all maintenance requests and repairs"
          icon={ToolOutlined}
        />

        <RepairsContent>
          <RepairStatsSection
            activeRepairs={activeRepairs}
            scheduledRepairs={scheduledRepairs}
            completedRepairs={completedRepairs}
          />

          <HandymenSection
            vendors={vendors}
            isVendorsLoading={isVendorsLoading}
            onAddHandyman={showAddHandymanModal}
            onEditHandyman={showEditHandymanModal}
          />

          <RepairTabsSection
            activeTab={activeTab}
            onTabChange={handleTabChange}
            activeRepairs={activeRepairs}
            scheduledRepairs={scheduledRepairs}
            completedRepairs={completedRepairs}
            onAddRepair={showRepairModal}
            onEditRepair={(repair) => {
              setEditingRepair(repair);
              setIsEditRepairModalVisible(true);
            }}
            draftActionsByTicketId={draftActionsByTicketId}
            onApproveDraft={handleApproveDraft}
          />
        </RepairsContent>
      </RepairsContainer>

      <HandymanModal
        visible={isAddHandymanModalVisible}
        onClose={closeAddHandymanModal}
        onSuccess={() => {
          // Cache invalidation in the modal will handle the refresh
        }}
      />
      <RepairModal
        visible={isAddRepairModalVisible}
        onClose={() => setIsAddRepairModalVisible(false)}
        onSuccess={() => {
          // Cache invalidation in the modal will handle the refresh
        }}
      />
      <HandymanModal
        visible={isEditHandymanModalVisible}
        handyman={editingHandyman}
        onClose={() => {
          setIsEditHandymanModalVisible(false);
          setEditingHandyman(null);
        }}
        onSuccess={handleEditHandymanSuccess}
      />

      <RepairModal
        visible={isEditRepairModalVisible}
        repair={editingRepair}
        onClose={() => {
          setIsEditRepairModalVisible(false);
          setEditingRepair(null);
        }}
        onSuccess={() => {
          /* refreshed via query invalidation */
        }}
      />
    </>
  );
};

// Styled Components
const RepairsContainer = styled.div`
  padding: 2rem;
  background: var(--color-bg-primary);
  min-height: 100vh;
  position: relative;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media ${device.mobile} {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    width: 90vw;
  }
`;

const RepairsContent = styled.div`
  min-height: 400px;
  position: relative;

  /* Add spacing between major sections */
  > * {
    margin-bottom: 2rem;
  }

  /* Reduce spacing for the last element */
  > *:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    min-height: 300px;

    /* Reduce spacing on mobile */
    > * {
      margin-bottom: 1.5rem;
    }
  }

  @media ${device.tablet} {
    padding: 0 1.5rem;
  }
`;

export default Repairs;
